import React from 'react';
import { UserProvider, useUser } from 'util/UserContext';
import { BrowserRouter as Router, Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom'
import Feed from 'components/Entities/Feed'
import AddIssue from 'components/Issues/AddIssue'
import IssueProfile from 'components/Issues/IssueProfile'
import IssueFeedSurface from 'components/Issues/IssueFeedSurface'
import IssueInfluenceSurface from 'components/Issues/IssueInfluenceSurface'
import UserProfile from 'components/User/UserProfile'
import SolutionProfile from 'components/Solutions/SolutionProfile'
import ImpactProfile from 'components/Impacts/ImpactProfile'
import AppBar from 'components/Navigation/AppBar'
import UserSettings from 'components/User/UserSettings'
import FeedbackProfile from 'components/Feedback/FeedbackProfile'
import AdminPortal from 'components/Admin/AdminPortal'
import PrivacyPolicy from './PrivacyPolicy'
import AuthModal from 'components/Auth/AuthModal'
import AuthLogin from 'components/Auth/AuthLogin'
import WelcomeModal from 'components/WelcomeModal';
import UsernameModal from 'components/User/UsernameModal';
import UserOnboardModal from 'components/User/UserOnboardModal';
import ActionProfile from 'components/Actions/ActionProfile';
import { storage } from 'util/storage';
import { useEffect, useState } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import { IssuePreviewProvider, useIssuePreviewContext } from 'context/IssuePreviewContext';
import NotFoundPage from 'components/ResponsePages/404NotFoundPage';
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage';
import { Toaster } from 'ui/Sonner';
import PasswordResetPage from './components/Auth/PasswordResetPage';
import TeamProfile from 'components/Teams/TeamProfile';
import TeamsTabs from 'components/Teams/TeamsTabs';
import TeamSettings from 'components/Teams/TeamSettings'
import Community from 'components/Common/Community';
import { ScrollToTop } from 'util/navUtils';
import CommentRedirect from 'components/Comments/CommentRedirect';
import CommentsDrawerProvider from 'components/Comments/CommentsDrawerProvider';
//import TeabateView from 'components/Teabates/TeabateView';
import GraphExplorer from 'components/Graph/GraphExplorer';
import FloatingFeedbackButton from 'components/Common/FloatingFeedbackButton';
import NotificationsModalWrapper from 'components/Notifications/NotificationsModalWrapper';
import { NotificationsModalProvider, useNotificationsModal } from 'context/NotificationsModalContext';
import { RealtimeToastsBridge } from 'hooks/useRealtimeToasts';
import { isPrerenderUserAgent } from 'util/prerenderUserAgents';
import { DrawerStackProvider, useDrawerStack } from 'ui/DrawerStack';
import { Drawer, DrawerContent } from 'ui/Drawer';
import { getDrawerStackLayerBase } from 'ui/layers';
import { AppChromeProvider, useAppChrome } from 'ui/AppChromeContext';
import useMobileViewport from 'hooks/useMobileViewport';
import { usePostAuthRedirect } from 'hooks/usePostAuthRedirect';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from 'lib/queryClient';
import { SocketProvider } from 'hooks/useSocket';
import ChatPage from 'components/Chat';
import GlobalSearchPage from 'components/Search/GlobalSearchPage';
import UserTasksPage from 'components/Tasks/UserTasksPage';
import StoryComposePage from 'components/Stories/StoryComposePage';
import UserStoriesPage from 'components/Stories/UserStoriesPage';
import CollectionProfile from 'components/Collections/CollectionProfile'
import QuizPage from 'components/Quiz/QuizPage';

// Dev-only kitchen-sink for design-system QA (#589); lazy so the chunk never
// loads in production (the route itself is also dev-gated below).
const DevStyleguidePage = React.lazy(() => import('components/Dev/DevStyleguidePage'));

/**
 * Renders all stacked drawer screens managed by the DrawerStack context.
 * Each screen is an independent Vaul Drawer with incrementing z-index.
 * Only active on mobile; on desktop all navigation is route-based.
 */
const DrawerStackRenderer: React.FC = () => {
    const { stack, pop } = useDrawerStack();
    const { userIsMobile } = useUser();

    if (!userIsMobile || stack.length === 0) {
        return null;
    }

    return (
        <>
            {stack.map((screen, index) => (
                <Drawer
                    key={screen.id}
                    open
                    onOpenChange={(open) => {
                        if (!open) {
                            pop();
                        }
                    }}
                    direction="right"
                    shouldScaleBackground={false}
                >
                    <DrawerContent
                        hideHandle
                        fullScreen
                        layerBase={getDrawerStackLayerBase(index)}
                        className="!left-auto !right-0 w-full focus-visible:outline-none !rounded-none !border-none bg-eoe-bg dark:bg-eoe-dark-bg"
                    >
                        {screen.type === "issueFeed" && (
                            <IssueFeedSurface stackCname={screen.cname} onStackClose={pop} />
                        )}
                        {screen.type === "issueInfluences" && (
                            <IssueInfluenceSurface stackCname={screen.cname} onStackClose={pop} />
                        )}
                    </DrawerContent>
                </Drawer>
            ))}
        </>
    );
};

const App = () => {
    return (
      <QueryClientProvider client={queryClient}>
        <UserProvider>
          <div className="min-h-screen bg-eoe-bg dark:!bg-eoe-dark-bg transition-colors duration-200 dark:text-gray-500">
            <Router>
              <SocketProvider>
                <NotificationsModalProvider>
                  <CommentsDrawerProvider>
                    <ScrollToTop>
                      <DrawerStackProvider>
                        <AppChromeProvider>
                          <AppShell />
                        </AppChromeProvider>
                      </DrawerStackProvider>
                    </ScrollToTop>
                  </CommentsDrawerProvider>
                </NotificationsModalProvider>
              </SocketProvider>
            </Router>
          </div>
        </UserProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    )
}

/**
 * AppShell houses route-aware UI such as the welcome modal.
 */
const AppShell = () => {
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { hiddenOnMobileCount } = useAppChrome();
    const isMobileViewport = useMobileViewport();
    // Centralized post-auth navigation: when a signed-out viewer
    // completes auth (via modal, standalone /signin page, or OAuth
    // callback), this hook consumes the pending destination that was
    // captured when the viewer first hit an auth wall and lands them
    // on that target URL. See ADR 015.
    usePostAuthRedirect();
    const isWelcomeRoute = location.pathname === "/welcome";
    const isGraphRoute = location.pathname === "/graph" || location.pathname.startsWith("/graph/");
    const chromeHiddenOnMobile = isMobileViewport && hiddenOnMobileCount > 0;
    const appShellClassName = chromeHiddenOnMobile
      ? "App h-full xl:ml-[260px] transition-colors duration-200"
      : "App h-full pt-14 xl:ml-[260px] transition-colors duration-200";

    // Welcome modal version - increment this when you want to show updated modal to existing users
    // How to update:
    // 1. Make changes to WelcomeModal.tsx content
    // 2. Increment CURRENT_WELCOME_MODAL_VERSION here
    // 3. Users who have seen older versions will automatically see the updated modal
    const CURRENT_WELCOME_MODAL_VERSION = 4;

    useEffect(() => {
        // Always allow the explicit /welcome route to show the modal.
        if (isWelcomeRoute) {
            setShowWelcomeModal(true);
            return;
        }

        // Skip auto-show for prerender user agents (bots).
        if (isPrerenderUserAgent()) {
            return;
        }

        // Get welcome modal version from localStorage (migrated from cookies).
        const userSeenVersion = storage.getJSON<number>("cookie:welcomeModalVersion", 0);
        
        // Show modal if user hasn't seen the current version.
        if (userSeenVersion < CURRENT_WELCOME_MODAL_VERSION) {
            setShowWelcomeModal(true);
        }
    }, [CURRENT_WELCOME_MODAL_VERSION, isWelcomeRoute]);

    /** Add body/html class on graph route so max-width can be overridden for GraphExplorer. */
    useEffect(() => {
        if (isGraphRoute) {
            document.documentElement.classList.add("graph-page");
            document.body.classList.add("graph-page");
        } else {
            document.documentElement.classList.remove("graph-page");
            document.body.classList.remove("graph-page");
        }
        return () => {
            document.documentElement.classList.remove("graph-page");
            document.body.classList.remove("graph-page");
        };
    }, [isGraphRoute]);

    const handleCloseWelcomeModal = () => {
        setShowWelcomeModal(false);
        // Store the current version in localStorage
        storage.setJSON("cookie:welcomeModalVersion", CURRENT_WELCOME_MODAL_VERSION);

        if (isWelcomeRoute) {
            navigate("/");
        }
    };

    return (
        <ThemeProvider>
          <IssuePreviewProvider>
            <Toaster />
            <WikipediaLinkInterceptor />
            <div className={appShellClassName}>
              {!chromeHiddenOnMobile && <AppBar />}
              <div>
                <Routes>
                  <Route path="/" element={<Feed />} />
                  <Route path="/welcome" element={<Feed />} />
                  {process.env.NODE_ENV === "development" ? (
                    // Dev-only kitchen-sink for design-system QA (#589); the
                    // lazy chunk and route are both absent from prod builds.
                    <Route
                      path="/dev/styleguide"
                      element={
                        <React.Suspense fallback={null}>
                          <DevStyleguidePage />
                        </React.Suspense>
                      }
                    />
                  ) : null}
                  <Route path="/quiz" element={<QuizPage />} />
                  <Route path="/quiz/:code" element={<QuizPage />} />
                  <Route path="/stories/:userId" element={<UserStoriesPage />} />
                  <Route path="/:entityType" element={<Feed />} />
                  <Route path="/:entityType/:sort" element={<Feed />} />
                  <Route path="/signin" element={<AuthLogin mode="signin" />} />
                  <Route path="/signup" element={<AuthLogin mode="signup" />} />
                  <Route
                    path="/add-issue"
                    element={<AddIssue open={false} onClose={() => {}} />}
                  />
                  <Route path="/issue/:cname/feed" element={<IssueFeedSurface />} />
                  <Route path="/issue/:cname/influences" element={<IssueInfluenceSurface />} />
                  <Route path="/issue/:cname/" element={<IssueProfile />} />
                  <Route path="/issue/:cname/:tab" element={<IssueProfile />} />
                  <Route path="/issue/:cname/comments/:commentId" element={<IssueProfile />} />
                  <Route path="/settings/" element={<UserSettings />} />
                  <Route path="/settings/:tab" element={<UserSettings />} />
                  <Route path="/user" element={<UserProfile />} />
                  <Route path="/user/:username" element={<UserProfile />} />
                  <Route path="/user/:username/:tab" element={<UserProfile />} />
                  {/* Solution routes */}
                  <Route path="/solution/:id" element={<SolutionProfile />} />
                  <Route path="/solution/:id/:tab" element={<SolutionProfile />} />
                  <Route path="/solution/:id/comments/:commentId" element={<SolutionProfile />} />
                  {/* Impact routes */}
                  <Route path="/impact/:id" element={<ImpactProfile />} />
                  <Route path="/impact/:id/:tab" element={<ImpactProfile />} />
                  <Route path="/impact/:id/comments/:commentId" element={<ImpactProfile />} />
                  {/* Teabate routes */}
                  <Route path="/teabate/:id" element={<ImpactProfile />} />
                  <Route path="/teabate/:id/:tab" element={<ImpactProfile />} />
                  <Route path="/teabate/:id/comments/:commentId" element={<ImpactProfile />} />
                  {/* Action routes */}
                  <Route path="/action/:id" element={<ActionProfile />} />
                  <Route path="/action/:id/:tab" element={<ActionProfile />} />
                  <Route path="/action/:id/comments/:commentId" element={<ActionProfile />} />
                  <Route path="/collection/:id" element={<CollectionProfile />} />
                  <Route path="/collection/:id/:tab" element={<CollectionProfile />} />
                  <Route path="/collection/:id/comments/:commentId" element={<CollectionProfile />} />
                  {/* Feedback routes */}
                  <Route path="/feedback/:id" element={<FeedbackProfile />} />
                  <Route path="/feedback/:id/:tab" element={<FeedbackProfile />} />
                  <Route path="/feedback/:id/comments/:commentId" element={<FeedbackProfile />} />
                  {/* Comment redirect route */}
                  <Route path="/comment/:id" element={<CommentRedirect />} />
                  {/* Admin routes */}
                  <Route path="/admin" element={<AdminPortal />} />
                  <Route path="/admin/:tab" element={<AdminPortal />} />
                  <Route
                    path="/policies/privacy-policy"
                    element={<PrivacyPolicy />}
                  />
                  <Route path="/communitea" element={<Community />} />
                  <Route path="/communitea/:tab" element={<Community />} />
                  {/* Legacy dashboard route kept as a redirect for shared links and bookmarks. */}
                  <Route
                    path="/community/dashboard"
                    element={<Navigate to="/communitea/dashboard" replace />}
                  />
                  {/* New Team Routes */}
                  <Route path="/parteas" element={<TeamsTabs />} />
                  <Route path="/parteas/:tab" element={<TeamsTabs />} />
                  <Route path="/partea/:teamname" element={<TeamProfile />} />
                  <Route path="/partea/:teamname/:tab" element={<TeamProfile />} />
                  <Route path="/partea/:teamname/settings" element={<TeamSettings />} />
                  <Route path="/partea/:teamname/settings/:tab" element={<TeamSettings />} />
                  {/*<Route path="/teabate/:id" element={<TeabateView />} />*/}
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/chat" element={<ChatPage />} />
                  <Route path="/chat/:conversationId" element={<ChatPage />} />
                  <Route path="/search" element={<GlobalSearchPage />} />
                  <Route path="/tasks" element={<UserTasksPage />} />
                  <Route path="/story/compose" element={<StoryComposePage />} />
                  <Route path="/404" element={<NotFoundPage />} />
                  <Route path="/500" element={<ServerErrorPage />} />
                  <Route path="/password" element={<AuthModal passwordReset={true} show={true} onClose={() => {}} />} />
                  <Route path="/passwordrecovery" element={<PasswordResetPage />} />
                  <Route path="/graph" element={<GraphExplorer />} />
                  <Route path="/graph/:cname" element={<GraphExplorer />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </div>
              <AuthModalWrapper />
              <UsernameModalWrapper />
              <UserOnboardModalWrapper />
              <NotificationsModalWrapper />
              <RealtimeToastsBridge />
              <WelcomeModal
                open={showWelcomeModal}
                onClose={handleCloseWelcomeModal}
              />
            </div>
            <FloatingFeedbackButton />
            <DrawerStackRenderer />
          </IssuePreviewProvider>
        </ThemeProvider>
    )
}

const AuthModalWrapper = () => {
  const { showAuthModal, setShowAuthModal, authModalMode, setAuthModalMode } = useUser();

  return (
    <>
      {showAuthModal && (
        <AuthModal
          show={showAuthModal}
          forcedAuthMode={authModalMode}
          onClose={() => {
            setShowAuthModal(false);
            setAuthModalMode(null);
          }}
        />
      )}
    </>
  );
};

const UsernameModalWrapper = () => {
  const { showUsernameModal, setShowUsernameModal } = useUser();

  return (
    <>
      {showUsernameModal && (
        <UsernameModal onClose={() => setShowUsernameModal(false)} userSignup={true} />
      )}
    </>
  );
};

const UserOnboardModalWrapper = () => {
  const { showUserOnboardModal, setShowUserOnboardModal } = useUser();
  return (
    <>
      {showUserOnboardModal && (
        <UserOnboardModal
          isOpen={showUserOnboardModal}
          onClose={() => setShowUserOnboardModal(false)}
        />
      )}
    </>
  );
};

/**
 * Route component for `/notifications`. Acts as a deep-link entrypoint:
 * opens the shared notifications modal on mount and, if the modal is
 * dismissed while the user is still on `/notifications`, navigates home
 * so the route does not leave a blank page behind. Mounting the modal
 * itself happens inside `NotificationsModalWrapper`, so this route only
 * orchestrates the global controller without owning UI state.
 */
const NotificationsPage = () => {
  const navigate = useNavigate();
  const { isOpen, open, close } = useNotificationsModal();
  const hasOpenedRef = React.useRef<boolean>(false);

  useEffect(() => {
    open();
    hasOpenedRef.current = true;
    return () => {
      close();
    };
  }, [open, close]);

  // If the user dismisses the modal (via close button, Esc, etc.) while
  // the route is still `/notifications`, fall back to home so we do not
  // strand them on a blank route. We only react after the initial open
  // has happened to avoid an immediate redirect on the first render.
  useEffect(() => {
    if (hasOpenedRef.current && !isOpen) {
      navigate("/");
    }
  }, [isOpen, navigate]);

  return null;
};

/**
 * WikipediaLinkInterceptor Component
 * 
 * Intercepts clicks on Wikipedia links and issue preview links throughout
 * the app and shows preview (drawer on mobile, tippy on desktop).
 */
const WikipediaLinkInterceptor = () => {
  const navigate = useNavigate();
  const { showPreview } = useIssuePreviewContext();

  useEffect(() => {
    // Don't intercept if showPreview is not available
    if (!showPreview) {
      return;
    }

    /**
     * Global click handler to intercept Wikipedia and issue preview links
     */
    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Find the closest anchor tag
      const anchor = target.closest('a');
      if (!anchor) {
        return;
      }

      // Don't intercept clicks inside the preview container itself
      // This allows Wikipedia links inside the preview to open normally
      const previewContainer = target.closest('[data-preview-container="true"]');
      if (previewContainer) {
        return;
      }

      // Check if it's an issue preview link (from transformHtmlContent)
      const issuePreviewCname = anchor.getAttribute('data-issue-preview');
      if (issuePreviewCname) {
        // Prevent default navigation
        event.preventDefault();
        event.stopPropagation();
        
        // Show preview for issue
        showPreview(issuePreviewCname, event).catch((error: Error) => {
          console.error('Error showing issue preview:', error);
          // Fallback to navigation if preview fails
          navigate(`/issue/${encodeURIComponent(issuePreviewCname)}`);
        });
        return;
      }

      // Check if it's a Wikipedia link
      if (!anchor.href) {
        return;
      }
      
      const href = anchor.href;
      const isWikiLink = href.includes('wikipedia.org/wiki/');
      
      if (!isWikiLink) {
        return;
      }

      // Intercept Wikipedia links on all devices to show preview
      // (mobile gets drawer, desktop gets tippy)
      
      // Prevent default navigation
      event.preventDefault();
      event.stopPropagation();

      // Show preview for Wikipedia link
      showPreview(href, event).catch((error: Error) => {
        console.error('Error showing preview:', error);
        // Fallback to opening in new tab if preview fails
        window.open(href, '_blank', 'noopener,noreferrer');
      });
    };

    // Add global click listener
    document.addEventListener('click', handleGlobalClick, true);

    // Cleanup
    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
    };
  }, [showPreview, navigate]);

  return null;
};

export default App;
